import { Fragment, useEffect, useState } from 'react'
import { Building2, Calendar, Car, Fuel, MapPin, CalendarDays, FileText, FileSpreadsheet } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'
import { Linha } from './LancamentoDepositos'
import { SeletorMultiplo } from './RelatorioConsumoArla'

interface Lancamento {
  data: string; numero: number; hora: number; veicod: string; motorista: string; ctf: string
  obscod: number; obsdes: string; posto: string; letra: string; notaFiscal: string
  litros: number; vun: number; valor: number; hodometro: number
}
interface Dia { data: string; itens: Lancamento[]; litros: number; valor: number }
interface Resultado { dias: Dia[]; registros: number; litros: number; valor: number }
interface Opcao { cod: string; des: string }

const fmtN = (v: number, c: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c })
const br = (d: string) => d.split('-').reverse().join('/')
const fmtHora = (h: number) => { const [hh, mm = '00'] = Number(h || 0).toFixed(2).split('.'); return `${hh.padStart(2, '0')}:${mm}` }
const hojeIso = () => new Date().toISOString().slice(0, 10)

// Lançamentos de combustível dia a dia (GeneXus WOCOMLAN → RDPS031).
export default function RelatorioLancamentosDia() {
  const { empresas } = useEmpresa()
  const [dataIni, setDataIni] = useState(hojeIso())
  const [dataFim, setDataFim] = useState(hojeIso())
  const [veiculo, setVeiculo] = useState('')
  const [postosSel, setPostosSel] = useState<string[]>([])
  const [combustivel, setCombustivel] = useState('')
  const [empresa, setEmpresa] = useState('0')
  const [postos, setPostos] = useState<Opcao[]>([])
  const [combustiveis, setCombustiveis] = useState<{ letra: string; nome: string }[]>([])
  const [res, setRes] = useState<Resultado | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erro, setErro] = useState('')

  useEffect(() => {
    api.get('/combustivel/postos').then(r => setPostos(r.data.map((p: any) => ({ cod: p.pracod, des: `${p.pracod} — ${p.pranom}` })))).catch(() => {})
    api.get('/combustivel/combustiveis-posto').then(r => setCombustiveis(r.data.filter((c: any) => c.letra !== 'I'))).catch(() => {})
  }, [])

  const params = () => ({ dataIni, dataFim, veiculo, postos: postosSel.join(','), combustivel, empresa })

  async function gerar() {
    setErro(''); setCarregando(true)
    try { setRes((await api.get('/combustivel/relatorios/lancamentos-dia', { params: params() })).data) }
    catch (e: any) { setRes(null); setErro(e?.response?.data?.error || 'Erro ao gerar relatório.') }
    finally { setCarregando(false) }
  }

  async function exportar(formato: 'pdf' | 'excel') {
    setErro(''); setGerando(formato)
    try {
      const r = await api.get(`/combustivel/relatorios/lancamentos-dia/${formato}`, { params: params(), responseType: 'blob' })
      const tipoArq = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipoArq }))
      if (formato === 'pdf') window.open(url, '_blank')
      else { const a = document.createElement('a'); a.href = url; a.download = 'lancamentos-combustivel-dia.xlsx'; a.click(); URL.revokeObjectURL(url) }
    } catch (e: any) {
      // Erro de validação vem como blob JSON.
      try { const t = await e?.response?.data?.text?.(); setErro(JSON.parse(t).error) } catch { setErro(formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.') }
    } finally { setGerando('') }
  }

  const th: React.CSSProperties = { padding: '0.4rem 0.4rem', fontSize: '0.66rem', fontWeight: 800, color: '#fff', background: '#1a6ea8', whiteSpace: 'nowrap', textAlign: 'left', position: 'sticky', top: 0, zIndex: 1 }
  const td: React.CSSProperties = { padding: '0.28rem 0.4rem', fontSize: '0.74rem', borderBottom: '1px solid #eef2f7', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }
  const num: React.CSSProperties = { ...td, textAlign: 'right' }
  const cols = ['Data', 'Número', 'Hora', 'Veículo', 'Motorista', 'Ctf', 'Ob', 'Observação', 'Pos', 'C', 'N.Fiscal', 'Litros', 'V.Unit', 'Valor', 'Hodôm.']
  const direita = new Set(['Número', 'Ob', 'N.Fiscal', 'Litros', 'V.Unit', 'Valor', 'Hodôm.'])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <CalendarDays size={18} color="#fff" />
            </span>
            <div>
              <h2>Lançamentos Dia a Dia</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Abastecimentos do período, com total por dia</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Período" icon={Calendar}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)} style={{ maxWidth: 170 }} />
              <span style={{ fontSize: '0.8rem', color: '#57606f' }}>a</span>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={{ maxWidth: 170 }} />
            </div>
          </Linha>
          <Linha label="Veículo" icon={Car}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ maxWidth: 140 }}>
                <LookupSelect endpoint="/erp/veiculos" codKey="veicod" descKey="veipla" tagKey="veicod"
                  codValue={veiculo} descValue={veiculo} onSelect={cod => setVeiculo(cod)} placeholder="Todos" />
              </div>
              {veiculo
                ? <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }} onClick={() => setVeiculo('')}>Todos</button>
                : <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todos</span>}
            </div>
          </Linha>
          <Linha label="Postos" icon={MapPin}>
            <SeletorMultiplo opcoes={postos} selecionados={postosSel} onChange={setPostosSel} placeholderBusca="Buscar posto..." />
          </Linha>
          <Linha label="Combustível" icon={Fuel}>
            <select value={combustivel} onChange={e => setCombustivel(e.target.value)} style={{ maxWidth: 200 }}>
              <option value="">Todos</option>
              {combustiveis.map(c => <option key={c.letra} value={c.letra}>{c.letra} — {c.nome}</option>)}
            </select>
          </Linha>
          <Linha label="Empresa" icon={Building2}>
            <select value={empresa} onChange={e => setEmpresa(e.target.value)} style={{ maxWidth: 280 }}>
              <option value="0">Todas</option>
              {empresas.map(e => <option key={e.empcod} value={e.empcod}>{e.empcod} — {e.empfan?.trim() || e.empnom?.trim()}</option>)}
            </select>
          </Linha>
          {erro && (
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>{erro}</div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button>
          <button className="btn" style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }} onClick={() => exportar('pdf')} disabled={!!gerando}>
            <FileText size={15} /> {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
          </button>
          <button className="btn" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }} onClick={() => exportar('excel')} disabled={!!gerando}>
            <FileSpreadsheet size={15} /> {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
          </button>
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo">
            <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap', fontSize: '0.8rem', color: '#475569', marginBottom: '0.7rem' }}>
              <span><b>{res.registros}</b> lançamento(s)</span>
              <span>Litros: <b style={{ color: '#1b3f7b' }}>{fmtN(res.litros, 3)}</b></span>
              <span>Valor: <b style={{ color: '#1b3f7b' }}>R$ {fmtN(res.valor, 2)}</b></span>
            </div>
            <div style={{ overflow: 'auto', maxHeight: '70vh', border: '1px solid #bfdbfe', borderRadius: 8 }}>
              <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                <thead>
                  <tr>{cols.map(c => <th key={c} style={{ ...th, textAlign: direita.has(c) ? 'right' : 'left' }}>{c}</th>)}</tr>
                </thead>
                <tbody>
                  {res.dias.map(d => (
                    <Fragment key={d.data}>
                      {d.itens.map((i, k) => {
                        const bg = k % 2 ? '#f5f9fd' : '#fff'
                        return (
                          <tr key={`${i.numero}-${k}`} style={{ background: bg }}>
                            <td style={{ ...td, color: '#1a6ea8' }}>{br(i.data)}</td>
                            <td style={num}>{i.numero}</td>
                            <td style={{ ...td, color: '#7c3aed' }}>{fmtHora(i.hora)}</td>
                            <td style={{ ...td, fontWeight: 700, color: '#1a6ea8' }}>{i.veicod}</td>
                            <td style={td}>{i.motorista}</td>
                            <td style={{ ...td, textAlign: 'center' }}>{i.ctf}</td>
                            <td style={num}>{i.obscod || ''}</td>
                            <td style={{ ...td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }} title={i.obsdes}>{i.obsdes}</td>
                            <td style={{ ...td, color: '#b91c1c' }}>{i.posto}</td>
                            <td style={{ ...td, textAlign: 'center', color: '#b91c1c' }}>{i.letra}</td>
                            <td style={num}>{i.notaFiscal}</td>
                            <td style={num}>{fmtN(i.litros, 3)}</td>
                            <td style={{ ...num, color: '#b91c1c' }}>{fmtN(i.vun, 3)}</td>
                            <td style={num}>{fmtN(i.valor, 2)}</td>
                            <td style={num}>{fmtN(i.hodometro, 0)}</td>
                          </tr>
                        )
                      })}
                      <tr style={{ background: '#eff6ff' }}>
                        <td colSpan={11} style={{ ...num, fontWeight: 800, color: '#1a6ea8', borderBottom: '2px solid #bfdbfe' }}>Total do Dia {br(d.data)}</td>
                        <td style={{ ...num, fontWeight: 800, color: '#1a6ea8', borderBottom: '2px solid #bfdbfe' }}>{fmtN(d.litros, 3)}</td>
                        <td style={{ ...num, borderBottom: '2px solid #bfdbfe' }} />
                        <td style={{ ...num, fontWeight: 800, color: '#1a6ea8', borderBottom: '2px solid #bfdbfe' }}>{fmtN(d.valor, 2)}</td>
                        <td style={{ ...num, borderBottom: '2px solid #bfdbfe' }} />
                      </tr>
                    </Fragment>
                  ))}
                  {res.dias.length === 0 && <tr><td colSpan={cols.length} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum lançamento no período.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
